import { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import CreatableSelect from 'react-select/creatable';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
  Table,
} from 'reactstrap';
import { EMR_LOGIN_URLS, NOTE_TYPES } from '../../constants/EmrNoteTypeMapping';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const extractUrlSubstring = (url) => {
  try {
    return new URL(url).hostname.split('.').slice(-2).join('.');
  } catch {
    return url;
  }
};

const EMR_OPTIONS = Object.entries(EMR_LOGIN_URLS)
  .map(([name, url]) => ({ name, urlSubstring: extractUrlSubstring(url) }))
  .sort((a, b) => a.name.localeCompare(b.name));

const LEVEL_META = {
  EMR: { label: 'EMR Level', color: 'primary' },
  Note: { label: 'Note-Type Level', color: 'info' },
  Section: { label: 'Section Level', color: 'warning' },
  'Note+Section': { label: 'Note + Section', color: 'danger' },
  Subsection: { label: 'Subsection Level', color: 'success' },
  'Note+Subsection': { label: 'Note + Subsection', color: 'dark' },
};

// Multiple note types are stored '|'-separated; backend matches any.
const NOTE_TYPE_SEP = '|';
const splitNoteTypes = (noteType) =>
  (noteType || '')
    .split(NOTE_TYPE_SEP)
    .map((s) => s.trim())
    .filter(Boolean);

function getLevel(noteType, section, subsection) {
  const hasNote = noteType?.trim().length > 0;
  const hasSection = section?.trim().length > 0;
  const hasSub = subsection?.trim().length > 0;
  if (hasNote && hasSection && hasSub) return 'Note+Subsection';
  if (hasSection && hasSub) return 'Subsection';
  if (hasNote && hasSection) return 'Note+Section';
  if (hasSection) return 'Section';
  if (hasNote) return 'Note';
  return 'EMR';
}

const EMPTY_FORM = {
  directive_id: null,
  emr_name: '',
  emr_url: '',
  note_type: '',
  section: '',
  subsection: '',
  directive_text: '',
};

// ─── Component ────────────────────────────────────────────────────────────────

const EMRDirectives = ({ apiClient }) => {
  const [activeTab, setActiveTab] = useState('beta');
  const [directives, setDirectives] = useState([]);
  const [prodDirectives, setProdDirectives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingProd, setLoadingProd] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [toDelete, setToDelete] = useState(null);
  const [search, setSearch] = useState('');
  const [searchProd, setSearchProd] = useState('');
  const [viewModal, setViewModal] = useState(false);
  const [viewDirective, setViewDirective] = useState(null);
  const [noteTypeOptions, setNoteTypeOptions] = useState([]);
  const [sectionOptions, setSectionOptions] = useState([]);
  const [subsectionsBySection, setSubsectionsBySection] = useState({});
  const [promotingIds, setPromotingIds] = useState(new Set());

  useEffect(() => {
    fetchDirectives();
    fetchProdDirectives();
  }, []);

  const fetchDirectives = async () => {
    setLoading(true);
    try {
      const res = await apiClient.listEMRDirectives();
      setDirectives(res?.directives || []);
    } catch {
      toast.error('Failed to load directives');
    } finally {
      setLoading(false);
    }
  };

  const fetchProdDirectives = async () => {
    setLoadingProd(true);
    try {
      const res = await apiClient.listEMRDirectivesProd();
      setProdDirectives(res?.directives || []);
    } catch {
      toast.error('Failed to load live directives');
    } finally {
      setLoadingProd(false);
    }
  };

  // Set of directive_ids where beta differs from live (any meaningful field)
  const unpromotedIds = useMemo(() => {
    const prodById = {};
    for (const d of prodDirectives) prodById[String(d.directive_id)] = d;
    const dirty = new Set();
    for (const d of directives) {
      const prod = prodById[String(d.directive_id)];
      if (
        !prod ||
        prod.directive_text !== d.directive_text ||
        prod.note_type !== d.note_type ||
        prod.section !== d.section ||
        prod.subsection !== d.subsection ||
        prod.emr_url !== d.emr_url
      )
        dirty.add(String(d.directive_id));
    }
    return dirty;
  }, [directives, prodDirectives]);

  // ── Modal helpers ────────────────────────────────────────────────────────

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = async (d) => {
    setForm({
      directive_id: d.directive_id,
      emr_name: d.emr_name || '',
      emr_url: d.emr_url || '',
      note_type: d.note_type || '',
      section: d.section || '',
      subsection: d.subsection || '',
      directive_text: d.directive_text || '',
    });
    if (d.emr_name) {
      try {
        const res = await apiClient.getEMRDirectiveOptions(
          d.emr_name,
          d.emr_url,
        );
        const baseNoteTypes = NOTE_TYPES.map((n) => n.name);
        const allNoteTypes = [
          ...new Set([...baseNoteTypes, ...(res?.note_types || [])]),
        ].sort();
        setNoteTypeOptions(allNoteTypes.map((v) => ({ value: v, label: v })));
        setSectionOptions(
          (res?.sections || []).map((v) => ({ value: v, label: v })),
        );
        setSubsectionsBySection(res?.subsections_by_section || {});
      } catch {
        setNoteTypeOptions(
          NOTE_TYPES.map((n) => ({ value: n.name, label: n.name })),
        );
        setSectionOptions([]);
        setSubsectionsBySection({});
      }
    }
    setModalOpen(true);
  };

  const confirmDelete = (d) => {
    setToDelete(d);
    setDeleteModal(true);
  };

  const handleChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  // ── Save ─────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!form.emr_url.trim()) {
      toast.warning('EMR URL is required');
      return;
    }
    if (!form.directive_text.trim()) {
      toast.warning('Directive text is required');
      return;
    }

    setSaving(true);
    try {
      if (form.directive_id) {
        await apiClient.editEMRDirective(
          form.directive_id,
          form.emr_name,
          form.emr_url,
          form.note_type,
          form.section,
          form.subsection,
          form.directive_text,
        );
        toast.success('Directive updated');
      } else {
        await apiClient.createEMRDirective(
          form.emr_name,
          form.emr_url,
          form.note_type,
          form.section,
          form.subsection,
          form.directive_text,
        );
        toast.success('Directive created');
      }
      setModalOpen(false);
      fetchDirectives();
    } catch {
      toast.error('Failed to save directive');
    } finally {
      setSaving(false);
    }
  };

  // ── Promote ──────────────────────────────────────────────────────────────

  const handlePromote = async (d) => {
    const id = String(d.directive_id);
    setPromotingIds((prev) => new Set(prev).add(id));
    try {
      const res = await apiClient.promoteEMRDirective(
        d.emr_name,
        d.directive_id,
      );
      if (res?.result === 'success') {
        toast.success('Directive promoted to live');
        await fetchProdDirectives();
      } else {
        toast.error(res?.error || 'Promote failed');
      }
    } catch {
      toast.error('Failed to promote directive');
    } finally {
      setPromotingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await apiClient.deleteEMRDirective(
        toDelete.directive_id,
        toDelete.emr_name,
      );
      toast.success('Directive deleted');
      setDeleteModal(false);
      setToDelete(null);
      fetchDirectives();
    } catch {
      toast.error('Failed to delete directive');
    }
  };

  // ── Filtered lists ────────────────────────────────────────────────────────

  const filtered = directives.filter((d) => {
    const q = search.toLowerCase();
    return (
      !q ||
      d.emr_name?.toLowerCase().includes(q) ||
      d.emr_url?.toLowerCase().includes(q) ||
      d.note_type?.toLowerCase().includes(q) ||
      d.section?.toLowerCase().includes(q) ||
      d.subsection?.toLowerCase().includes(q) ||
      d.directive_text?.toLowerCase().includes(q)
    );
  });

  const filteredProd = prodDirectives.filter((d) => {
    const q = searchProd.toLowerCase();
    return (
      !q ||
      d.emr_name?.toLowerCase().includes(q) ||
      d.emr_url?.toLowerCase().includes(q) ||
      d.note_type?.toLowerCase().includes(q) ||
      d.section?.toLowerCase().includes(q) ||
      d.subsection?.toLowerCase().includes(q) ||
      d.directive_text?.toLowerCase().includes(q)
    );
  });

  const level = getLevel(form.note_type, form.section, form.subsection);
  const levelMeta = LEVEL_META[level];

  // ── Shared table renderer ─────────────────────────────────────────────────

  const renderTable = (rows, isReadOnly = false, isLoadingRows = false) => {
    if (isLoadingRows) {
      return (
        <div className="text-center py-5">
          <Spinner color="primary" />
        </div>
      );
    }
    if (rows.length === 0) {
      return (
        <div className="text-center py-5 text-muted">
          {isReadOnly
            ? 'Nothing promoted yet.'
            : search
              ? 'No directives match your search.'
              : 'No directives yet. Click "New Directive" to add one.'}
        </div>
      );
    }
    return (
      <Table responsive className="align-items-center">
        <thead className="thead-light">
          <tr>
            <th>EMR</th>
            <th>URL Pattern</th>
            <th>Note Type</th>
            <th>Section</th>
            <th>Subsection</th>
            <th>Level</th>
            <th>Preview</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => {
            const lvl = getLevel(d.note_type, d.section, d.subsection);
            const meta = LEVEL_META[lvl];
            const id = String(d.directive_id);
            const isUnpromoted = unpromotedIds.has(id);
            const isPromoting = promotingIds.has(id);
            return (
              <tr key={d.directive_id}>
                <td>
                  <strong>{d.emr_name || '—'}</strong>
                </td>
                <td>
                  <code className="small">{d.emr_url}</code>
                </td>
                <td>
                  {splitNoteTypes(d.note_type).join(', ') || (
                    <span className="text-muted">Any</span>
                  )}
                </td>
                <td>{d.section || <span className="text-muted">Any</span>}</td>
                <td>
                  {d.subsection || <span className="text-muted">—</span>}
                </td>
                <td>
                  <Badge color={meta.color} pill>
                    {meta.label}
                  </Badge>
                </td>
                <td style={{ maxWidth: 260 }}>
                  <span
                    className="text-muted small"
                    style={{ whiteSpace: 'pre-wrap' }}
                  >
                    {d.directive_text?.slice(0, 80)}
                    {d.directive_text?.length > 80 ? '…' : ''}
                  </span>
                </td>
                {isReadOnly ? (
                  <td>
                    <Button
                      size="sm"
                      color="secondary"
                      outline
                      onClick={() => {
                        setViewDirective(d);
                        setViewModal(true);
                      }}
                    >
                      View
                    </Button>
                  </td>
                ) : (
                  <td>
                    <Button
                      size="sm"
                      color="primary"
                      outline
                      className="mr-2"
                      onClick={() => openEdit(d)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      color={isUnpromoted ? 'danger' : 'success'}
                      className="mr-2"
                      disabled={isPromoting}
                      onClick={() => handlePromote(d)}
                      title={
                        isUnpromoted
                          ? 'Not yet live — click to promote'
                          : 'Up to date with live'
                      }
                    >
                      {isPromoting ? (
                        <Spinner size="sm" />
                      ) : isUnpromoted ? (
                        <>
                          <i className="fas fa-arrow-up mr-1" />
                          Promote
                        </>
                      ) : (
                        <strong>Promoted</strong>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      color="danger"
                      outline
                      onClick={() => confirmDelete(d)}
                    >
                      Delete
                    </Button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Card className="shadow">
        <CardHeader className="border-0 d-flex align-items-center justify-content-between">
          <div>
            <h3 className="mb-0">EMR Directives</h3>
            <p className="text-muted small mb-0 mt-1">
              System-level instructions appended to AI prompts per EMR, note
              type, and section.
            </p>
          </div>
          {activeTab === 'beta' && (
            <Button color="primary" size="sm" onClick={openCreate}>
              + New Directive
            </Button>
          )}
        </CardHeader>

        <CardBody>
          {/* Tabs */}
          <Nav tabs className="mb-3">
            <NavItem>
              <NavLink
                className={activeTab === 'beta' ? 'active' : ''}
                style={{ cursor: 'pointer' }}
                onClick={() => setActiveTab('beta')}
              >
                Beta Directives
                {unpromotedIds.size > 0 && (
                  <Badge
                    color="danger"
                    pill
                    className="ml-2"
                    style={{ fontSize: 10 }}
                  >
                    {unpromotedIds.size} unpromoted
                  </Badge>
                )}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === 'prod' ? 'active' : ''}
                style={{ cursor: 'pointer' }}
                onClick={() => setActiveTab('prod')}
              >
                Live (Promoted)
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab}>
            {/* ── Beta tab ─────────────────────────────────────────────────── */}
            <TabPane tabId="beta">
              <Row className="mb-3">
                <Col md={4}>
                  <Input
                    placeholder="Search directives..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    bsSize="sm"
                  />
                </Col>
                <Col className="d-flex align-items-center">
                  <span className="text-muted small">
                    {filtered.length} directive
                    {filtered.length !== 1 ? 's' : ''}
                  </span>
                </Col>
              </Row>

              {/* Targeting levels legend */}
              <div className="d-flex flex-wrap mb-3">
                {Object.entries(LEVEL_META).map(([key, { label, color }]) => (
                  <Badge
                    key={key}
                    color={color}
                    pill
                    className="px-3 py-1 mr-2 mb-1"
                  >
                    {label}
                  </Badge>
                ))}
                <span className="text-muted small align-self-center ml-1">
                  — specificity increases left → right
                </span>
              </div>

              {renderTable(filtered, false, loading)}
            </TabPane>

            {/* ── Live/Prod tab ────────────────────────────────────────────── */}
            <TabPane tabId="prod">
              <Row className="mb-3">
                <Col md={4}>
                  <Input
                    placeholder="Search live directives..."
                    value={searchProd}
                    onChange={(e) => setSearchProd(e.target.value)}
                    bsSize="sm"
                  />
                </Col>
                <Col className="d-flex align-items-center">
                  <span className="text-muted small">
                    {filteredProd.length} directive
                    {filteredProd.length !== 1 ? 's' : ''}
                  </span>
                  <span className="text-muted small ml-3">
                    <i className="fas fa-lock mr-1" />
                    Read-only — promote from the Beta tab to update
                  </span>
                </Col>
              </Row>

              {/* Targeting levels legend */}
              <div className="d-flex flex-wrap mb-3">
                {Object.entries(LEVEL_META).map(([key, { label, color }]) => (
                  <Badge
                    key={key}
                    color={color}
                    pill
                    className="px-3 py-1 mr-2 mb-1"
                  >
                    {label}
                  </Badge>
                ))}
                <span className="text-muted small align-self-center ml-1">
                  — specificity increases left → right
                </span>
              </div>

              {renderTable(filteredProd, true, loadingProd)}
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>

      {/* ── Create / Edit Modal ────────────────────────────────────────────── */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setModalOpen(false)}>
          {form.directive_id ? 'Edit Directive' : 'New Directive'}
        </ModalHeader>
        <ModalBody>
          {/* Targeting level badge */}
          <div className="mb-3">
            <Badge color={levelMeta.color} pill className="px-3 py-2">
              {levelMeta.label}
            </Badge>
            <span className="text-muted small ml-2">
              Fill in Note Type / Section to target more specifically.
            </span>
          </div>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>
                  EMR <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  value={form.emr_name}
                  onChange={async (e) => {
                    const selected = EMR_OPTIONS.find(
                      (o) => o.name === e.target.value,
                    );
                    if (selected) {
                      handleChange('emr_name', selected.name);
                      handleChange('emr_url', selected.urlSubstring);
                      handleChange('note_type', '');
                      handleChange('section', '');
                      handleChange('subsection', '');
                      try {
                        const res = await apiClient.getEMRDirectiveOptions(
                          selected.name,
                          selected.urlSubstring,
                        );
                        const baseNoteTypes = NOTE_TYPES.map((n) => n.name);
                        const allNoteTypes = [
                          ...new Set([
                            ...baseNoteTypes,
                            ...(res?.note_types || []),
                          ]),
                        ].sort();
                        setNoteTypeOptions(
                          allNoteTypes.map((v) => ({ value: v, label: v })),
                        );
                        setSectionOptions(
                          (res?.sections || []).map((v) => ({
                            value: v,
                            label: v,
                          })),
                        );
                        setSubsectionsBySection(res?.subsections_by_section || {});
                      } catch {
                        setNoteTypeOptions(
                          NOTE_TYPES.map((n) => ({
                            value: n.name,
                            label: n.name,
                          })),
                        );
                        setSectionOptions([]);
                        setSubsectionsBySection({});
                      }
                    } else {
                      handleChange('emr_name', '');
                      handleChange('emr_url', '');
                      handleChange('subsection', '');
                      setNoteTypeOptions([]);
                      setSectionOptions([]);
                      setSubsectionsBySection({});
                    }
                  }}
                >
                  <option value="">— Select EMR —</option>
                  {EMR_OPTIONS.map((o) => (
                    <option key={o.name} value={o.name}>
                      {o.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>
                  Note Type{' '}
                  <span className="text-muted">(optional — blank = all)</span>
                </Label>
                <CreatableSelect
                  isMulti
                  isClearable
                  placeholder="e.g. Daily Note, Evaluation"
                  options={noteTypeOptions}
                  value={splitNoteTypes(form.note_type).map((v) => ({
                    value: v,
                    label: v,
                  }))}
                  onChange={(opts) =>
                    handleChange(
                      'note_type',
                      (opts || []).map((o) => o.value).join(NOTE_TYPE_SEP),
                    )
                  }
                />
                <small className="text-muted">
                  Case-insensitive substring match; matches any selected. Type
                  to search or add custom.
                </small>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>
                  Section Prefix{' '}
                  <span className="text-muted">(optional — blank = all)</span>
                </Label>
                <CreatableSelect
                  isClearable
                  placeholder="e.g. TREATMENTS, ROM"
                  options={sectionOptions}
                  value={
                    form.section
                      ? { value: form.section, label: form.section }
                      : null
                  }
                  onChange={(opt) => {
                    handleChange('section', opt ? opt.value : '');
                    handleChange('subsection', '');
                  }}
                />
                <small className="text-muted">
                  Case-insensitive match. Type to search or add custom.
                </small>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>View More Subsection <span className="text-muted">(optional — requires Section)</span></Label>
                <CreatableSelect
                  isClearable
                  isDisabled={!form.section.trim()}
                  placeholder="e.g. Pain Level, ROM"
                  options={(subsectionsBySection[form.section] || []).map((v) => ({ value: v, label: v }))}
                  value={form.subsection ? { value: form.subsection, label: form.subsection } : null}
                  onChange={(opt) => handleChange('subsection', opt ? opt.value : '')}
                />
                <small className="text-muted">
                  Subsection names are loaded from the template library. Type to add a custom name.
                </small>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label>
              Directive Text <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              rows={12}
              placeholder="Write the instructions for the AI here..."
              value={form.directive_text}
              onChange={(e) => handleChange('directive_text', e.target.value)}
              style={{ fontFamily: 'monospace', fontSize: 13 }}
            />
            <small className="text-muted">
              This text is appended to the system prompt when the EMR / note
              type / section matches.
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" className="mr-1" /> : null}
            {form.directive_id ? 'Save Changes' : 'Create Directive'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── View Directive Modal ──────────────────────────────────────────── */}
      <Modal isOpen={viewModal} toggle={() => setViewModal(false)} size="lg">
        <ModalHeader toggle={() => setViewModal(false)}>
          {viewDirective?.emr_name || viewDirective?.emr_url}
          {viewDirective?.note_type && (
            <span className="text-muted ml-2 small">
              · {splitNoteTypes(viewDirective.note_type).join(', ')}
            </span>
          )}
          {viewDirective?.section && (
            <span className="text-muted ml-2 small">
              · {viewDirective.section}
            </span>
          )}
          {viewDirective?.subsection && (
            <span className="text-muted ml-2 small">
              · {viewDirective.subsection}
            </span>
          )}
        </ModalHeader>
        <ModalBody>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: 13,
              margin: 0,
            }}
          >
            {viewDirective?.directive_text}
          </pre>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setViewModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── Delete Confirmation Modal ──────────────────────────────────────── */}
      <Modal
        isOpen={deleteModal}
        toggle={() => setDeleteModal(false)}
        size="sm"
      >
        <ModalHeader toggle={() => setDeleteModal(false)}>
          Delete Directive
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete the directive for{' '}
          <strong>{toDelete?.emr_name || toDelete?.emr_url}</strong>? This
          cannot be undone.
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            onClick={() => setDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button color="danger" onClick={handleDelete}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default EMRDirectives;
